import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { authApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Stamp } from 'lucide-react';
import { toast } from 'sonner';

export default function PdfCompanyPage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ pdf_company_name: '', pdf_company_enabled: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        pdf_company_name: user.pdf_company_name || '',
        pdf_company_enabled: user.pdf_company_enabled || false,
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authApi.updateMe(form);
      await refreshUser();
      toast.success('PDF-Firmenname gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="pdf-company-page">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <Stamp className="h-7 w-7 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">PDF-Firmenname</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Eigenen Firmennamen in generierten PDF-Dokumenten verwenden</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Firmenname in PDFs</CardTitle>
          <CardDescription>
            Wenn aktiviert, wird Ihr Firmenname in allen generierten PDF-Dokumenten anstelle des Standard-Firmennamens angezeigt. Wenn deaktiviert, bleibt das Feld leer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div>
              <Label htmlFor="pdf-toggle" className="font-medium">Eigenen Firmennamen verwenden</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Wenn deaktiviert, bleibt das Firmenfeld in PDFs leer</p>
            </div>
            <Switch
              id="pdf-toggle"
              checked={form.pdf_company_enabled}
              onCheckedChange={v => setForm(f => ({ ...f, pdf_company_enabled: v }))}
              data-testid="pdf-company-toggle"
            />
          </div>
          {form.pdf_company_enabled && (
            <div className="space-y-2">
              <Label htmlFor="pdf-company-name">Firmenname für PDF</Label>
              <Input
                id="pdf-company-name"
                value={form.pdf_company_name}
                onChange={e => setForm(f => ({ ...f, pdf_company_name: e.target.value }))}
                placeholder="z.B. Meine Hausverwaltung GmbH"
                data-testid="pdf-company-name-input"
              />
              <p className="text-xs text-muted-foreground">Dieser Name erscheint im Header und Footer Ihrer PDF-Dokumente.</p>
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} data-testid="pdf-company-save-btn">
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
